import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import FastfoodOutlinedIcon from '@mui/icons-material/FastfoodOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import dayjs from 'dayjs';

import CurrencyAmount from 'components/currency/CurrencyAmount';
import { orderStatusChipSx, paymentStatusChipSx } from 'components/table/tableChipStyles';
import { getOrderPaymentDetail, getOfferDetail } from 'services/order.service';
import { brand, destructive } from 'utils/designTokens';

export default function OrderDetailsDialog({ open, order, onClose, onOpenPaymentRefund }) {
  const [paymentData, setPaymentData] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadExtraDetails() {
      if (!open || !order?.id) {
        setPaymentData(null);
        setOfferData(null);
        return;
      }

      setLoading(true);
      try {
        const [paymentRes, offerRes] = await Promise.all([
          getOrderPaymentDetail(order.id).catch(() => null),
          order.offer_id ? getOfferDetail(order.offer_id).catch(() => null) : Promise.resolve(null)
        ]);

        if (isMounted) {
          if (paymentRes?.data) setPaymentData(paymentRes.data);
          if (offerRes?.data) setOfferData(offerRes.data);
        }
      } catch (err) {
        console.warn('Failed loading extra order details:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadExtraDetails();
    return () => {
      isMounted = false;
    };
  }, [open, order]);

  if (!order) return null;

  const items = Array.isArray(order.items) ? order.items : [];
  const isDelivery = String(order.mode).toLowerCase() === 'delivery';
  const hasOffer = Boolean(order.offer_id || Number(order.coupon_discount) > 0);

  // Authoritative financials:
  const subTotal = Number(order.sub_total || 0);
  const itemDiscounts = Number(order.total_discount || 0);
  const couponDiscount = Number(order.coupon_discount || 0);
  const deliveryCharge = Number(order.delivery_charge || 0);
  const taxTotal = Number(order.tax_total || 0);
  const grandTotal = Number(order.grand_total != null ? order.grand_total : order.total || 0);
  const paidAmount = Number(paymentData?.financials?.paid_amount ?? order.paid ?? 0);
  const refundedAmount = Number(paymentData?.financials?.refunded_amount || 0);
  const isTerminalOrder = ['cancelled', 'rejected'].includes(String(order.order_status || '').toLowerCase());
  const balanceDue = isTerminalOrder && paidAmount === 0
    ? 0
    : Number(
        paymentData?.financials?.balance_due != null
          ? paymentData.financials.balance_due
          : (order.balance_due != null ? order.balance_due : Math.max(0, grandTotal - paidAmount))
      );

  const paymentMethodName =
    paymentData?.payment_method?.name ||
    (paymentData?.transaction?.gateway === 'stripe' ? 'Credit / Debit Card (Stripe)' : isDelivery && paidAmount === 0 ? 'Cash On Delivery' : 'Direct Payment');

  const paymentMethodType =
    paymentData?.payment_method?.type || (paymentData?.transaction?.gateway === 'stripe' ? 'card' : 'cash');

  const isCardPayment = paymentMethodType === 'card' || paymentData?.transaction?.gateway === 'stripe';
  const customer = paymentData?.customer || order.customer;
  const store = paymentData?.store;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <ReceiptLongOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {order.order_no || `#${order.id}`}
              </Typography>
              {order.token_no != null && (
                <Typography variant="caption" color="text.secondary">
                  Token #{order.token_no} · Created {order.createdAt ? dayjs(order.createdAt).format('YYYY-MM-DD HH:mm') : '—'}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={isDelivery ? 'Delivery' : 'Takeaway'}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 600,
                borderColor: isDelivery ? 'primary.main' : 'warning.main',
                color: isDelivery ? 'primary.main' : 'warning.dark'
              }}
            />
            {order.order_status && (
              <Chip
                label={order.order_status}
                size="small"
                sx={orderStatusChipSx(order.order_status)}
              />
            )}
            {order.payment_status && (
              <Chip
                label={order.payment_status}
                size="small"
                sx={paymentStatusChipSx(order.payment_status)}
              />
            )}
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2, pb: 3 }}>
        {loading && !paymentData && (
          <Box display="flex" justifyContent="center" alignItems="center" py={2}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Synchronizing authoritative backend payment details...
            </Typography>
          </Box>
        )}

        <Stack spacing={2.5}>
          {/* Top Key Metrics Banner */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: brand.canvas,
              borderColor: brand.border
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                  Grand Total
                </Typography>
                <CurrencyAmount value={grandTotal} variant="h6" fontWeight={700} color="primary.main" />
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                  Amount Paid
                </Typography>
                <CurrencyAmount value={paidAmount} variant="h6" fontWeight={700} color="#2e7d32" />
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                  Outstanding Balance
                </Typography>
                <CurrencyAmount
                  value={balanceDue}
                  variant="h6"
                  fontWeight={700}
                  color={balanceDue > 0 ? destructive.main : 'text.primary'}
                />
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                  Refunded Amount
                </Typography>
                <CurrencyAmount value={refundedAmount} variant="h6" fontWeight={700} color="text.secondary" />
              </Grid>
            </Grid>
          </Paper>

          {/* Section 1 & 2: Order & Customer Information */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <PersonOutlineOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Customer Details
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Name:</Typography>
                    <Typography variant="body2" fontWeight={600}>{customer?.name || 'Guest'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Mobile Phone:</Typography>
                    <Typography variant="body2" fontWeight={500}>{customer?.mobile_number || '—'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Email Address:</Typography>
                    <Typography variant="body2">{customer?.email || '—'}</Typography>
                  </Stack>
                  {customer?.id && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Customer ID:</Typography>
                      <Typography variant="caption" fontFamily="monospace">{customer.id}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* Section 3 & 4: Store & Delivery / Mode Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <StorefrontOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {isDelivery ? 'Delivery & Store Info' : 'Takeaway & Store Info'}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Fulfillment Mode:</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {isDelivery ? (
                        <LocalShippingOutlinedIcon color="primary" sx={{ fontSize: 18 }} />
                      ) : (
                        <FastfoodOutlinedIcon color="warning" sx={{ fontSize: 18 }} />
                      )}
                      <Typography variant="body2" fontWeight={700}>
                        {isDelivery ? 'Home Delivery' : 'In-Store Takeaway'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Store Name:</Typography>
                    <Typography variant="body2" fontWeight={600}>{store?.name || 'Main Kitchen'}</Typography>
                  </Stack>
                  {store?.address && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Store Address:</Typography>
                      <Typography variant="caption">{store.address}</Typography>
                    </Stack>
                  )}
                  {isDelivery ? (
                    <Box sx={{ mt: 0.5, p: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Delivery Contact: {customer?.mobile_number || 'See customer phone'}
                      </Typography>
                      <Stack direction="row" spacing={0.3} alignItems="center" component="span">
                        <Typography variant="caption" color="text.secondary">Delivery Charge:</Typography>
                        <CurrencyAmount value={deliveryCharge} variant="caption" color="text.secondary" decimals={2} />
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 0.5, p: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary">
                        Takeaway Order: Customer will collect items directly at store counter.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Section 5: Order Items Breakdown */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: brand.canvas, borderBottom: `1px solid ${brand.border}` }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Ordered Items ({items.length})
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                    <TableCell>Product Item</TableCell>
                    <TableCell align="center">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Unit Discount</TableCell>
                    <TableCell align="right">Item Discount</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No items recorded in this order.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((line, idx) => {
                      const unitPrice = Number(line.price || 0);
                      const unitDisc = Number(line.discount_price || 0);
                      const itemDisc = Number(line.discount_amount || unitDisc * Number(line.qty || 1));
                      const lineTotal = Number(line.total != null ? line.total : (unitPrice - unitDisc) * Number(line.qty || 1));

                      return (
                        <TableRow key={line.id || idx}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {line.product?.name || line.name || 'Unnamed Product'}
                            </Typography>
                            {line.variant?.name && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Variant: {line.variant.name}
                              </Typography>
                            )}
                            {line.note && (
                              <Typography variant="caption" color="primary.main" display="block">
                                Note: {line.note}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600}>
                              {line.qty}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <CurrencyAmount value={unitPrice} />
                          </TableCell>
                          <TableCell align="right">
                            {unitDisc > 0 ? (
                              <CurrencyAmount value={unitDisc} prefix="-" color="success.main" variant="caption" fontWeight={600} decimals={2} align="flex-end" />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {itemDisc > 0 ? (
                              <CurrencyAmount value={itemDisc} prefix="-" color="success.main" variant="caption" fontWeight={600} decimals={2} align="flex-end" />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <CurrencyAmount value={lineTotal} fontWeight={700} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Section 6: Offers and Coupons Details */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <ConfirmationNumberOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700}>
                Offers &amp; Promotion Applied
              </Typography>
            </Stack>

            {hasOffer ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(46, 125, 50, 0.06)',
                  border: '1px solid rgba(46, 125, 50, 0.2)'
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Promotion Type:</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.dark">
                      {offerData?.offer_type === 'coupon' || offerData?.coupon_code ? 'Coupon Voucher' : 'Store Promotion / Offer'}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Coupon Code / Title:</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {offerData?.coupon_code ? (
                        <Chip
                          label={offerData.coupon_code}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : (
                        offerData?.offer_name_en || 'Applied Offer'
                      )}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Discount Applied:</Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <CurrencyAmount value={couponDiscount} prefix="-" color="success.main" variant="body2" fontWeight={700} decimals={2} />
                    </Box>
                  </Grid>

                  {offerData?.description && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">
                        {offerData.description}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Alert severity="info" sx={{ bgcolor: brand.canvas, border: `1px solid ${brand.border}` }}>
                No coupon or offer applied to this order.
              </Alert>
            )}
          </Paper>

          {/* Section 7 & 8: Payment Information & Financial Breakdown */}
          <Grid container spacing={2}>
            {/* Section 7: Payment Details */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <CreditCardOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Payment Information
                  </Typography>
                </Stack>

                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {paymentMethodName}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
                    {order.payment_status && (
                      <Chip
                        label={order.payment_status}
                        size="small"
                        sx={paymentStatusChipSx(order.payment_status)}
                      />
                    )}
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Collection Method:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {isCardPayment ? 'Online Card Transaction' : 'Cash On Delivery (Driver Collection)'}
                    </Typography>
                  </Stack>

                  {/* Card specific fields */}
                  {isCardPayment && paymentData?.transaction && (
                    <>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Gateway Provider:</Typography>
                        <Typography variant="caption" fontWeight={600}>Stripe Payments</Typography>
                      </Stack>

                      {paymentData.transaction.stripe_charge_id && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Stripe Charge:</Typography>
                          <Typography
                            variant="caption"
                            fontFamily="monospace"
                            sx={{ bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1 }}
                          >
                            {paymentData.transaction.stripe_charge_id}
                          </Typography>
                        </Stack>
                      )}

                      {paymentData.transaction.paid_at && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Captured At:</Typography>
                          <Typography variant="caption">
                            {dayjs(paymentData.transaction.paid_at).format('YYYY-MM-DD HH:mm')}
                          </Typography>
                        </Stack>
                      )}
                    </>
                  )}

                  {/* COD specific status */}
                  {!isCardPayment && (
                    <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {paidAmount >= grandTotal ? (
                          'Cash has been collected and reconciled by administration.'
                        ) : (
                          <Stack direction="row" spacing={0.3} alignItems="center" component="span" flexWrap="wrap">
                            <span>Outstanding cash collection due upon delivery:</span>
                            <CurrencyAmount value={balanceDue} variant="caption" color="text.secondary" decimals={2} />
                            <span>.</span>
                          </Stack>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* Section 8: Financial Summary Ledger */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <AccountBalanceWalletOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Financial Summary
                  </Typography>
                </Stack>

                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Items Subtotal:</Typography>
                    <CurrencyAmount value={subTotal} fontWeight={500} />
                  </Stack>

                  {itemDiscounts > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="success.main">Item Offers Discount:</Typography>
                      <CurrencyAmount value={itemDiscounts} prefix="-" color="success.main" variant="body2" fontWeight={600} decimals={2} />
                    </Stack>
                  )}

                  {couponDiscount > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="success.main">Coupon Voucher Discount:</Typography>
                      <CurrencyAmount value={couponDiscount} prefix="-" color="success.main" variant="body2" fontWeight={600} decimals={2} />
                    </Stack>
                  )}

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Delivery Charge:</Typography>
                    <CurrencyAmount value={deliveryCharge} fontWeight={500} />
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Taxes (VAT 5%):</Typography>
                    <CurrencyAmount value={taxTotal} fontWeight={500} />
                  </Stack>

                  <Divider sx={{ my: 0.75 }} />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle1" fontWeight={700}>Final Grand Total:</Typography>
                    <CurrencyAmount value={grandTotal} variant="subtitle1" fontWeight={700} color="primary.main" />
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Amount Paid:</Typography>
                    <CurrencyAmount value={paidAmount} fontWeight={600} color="#2e7d32" />
                  </Stack>

                  {refundedAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="error.main">Amount Refunded:</Typography>
                      <CurrencyAmount value={refundedAmount} prefix="-" color="error.main" variant="body2" fontWeight={600} decimals={2} />
                    </Stack>
                  )}

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={700}>Balance Due:</Typography>
                    <CurrencyAmount
                      value={balanceDue}
                      fontWeight={700}
                      color={balanceDue > 0 ? destructive.main : 'text.primary'}
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Section 9: Order Status & Audit Timeline */}
          {((order.latest_status && order.latest_status.order_status) || (Array.isArray(paymentData?.refund_history) && paymentData.refund_history.length > 0)) && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <HistoryOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Status History &amp; Audit Logs
                </Typography>
              </Stack>

              <Stack spacing={1}>
                {order.latest_status?.order_status && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Status: {order.latest_status.order_status}
                      </Typography>
                      {order.latest_status.note && (
                        <Typography variant="caption" color="text.secondary">
                          {order.latest_status.note}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {order.latest_status.timestamp ? dayjs(order.latest_status.timestamp).format('YYYY-MM-DD HH:mm') : '—'}
                    </Typography>
                  </Stack>
                )}

                {Array.isArray(paymentData?.refund_history) && paymentData.refund_history.map((h, i) => (
                  <Stack key={h.id || i} direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1, borderTop: '1px dashed #e0e0e0' }}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={h.payment_status} size="small" sx={paymentStatusChipSx(h.payment_status)} />
                        <CurrencyAmount value={h.amount} variant="body2" fontWeight={600} decimals={2} />
                      </Stack>
                      {h.note && (
                        <Typography variant="caption" color="text.secondary">
                          {h.note}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {h.timestamp ? dayjs(h.timestamp).format('YYYY-MM-DD HH:mm') : '—'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Notes (Order note or Kitchen note) */}
          {(order.note || paymentData?.order?.kitchen_note) && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas }}>
              {order.note && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Customer Note:</strong> {order.note}
                </Typography>
              )}
              {paymentData?.order?.kitchen_note && (
                <Typography variant="body2" color="text.secondary" mt={order.note ? 0.5 : 0}>
                  <strong>Kitchen Note:</strong> {paymentData.order.kitchen_note}
                </Typography>
              )}
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>

        {isCardPayment && onOpenPaymentRefund && (
          <Button
            variant="contained"
            startIcon={<CurrencyExchangeIcon />}
            onClick={() => {
              onClose();
              onOpenPaymentRefund(order);
            }}
            sx={{
              bgcolor: brand.primary,
              '&:hover': { bgcolor: brand.primaryDark }
            }}
          >
            Payment &amp; Refund Controls
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

OrderDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onOpenPaymentRefund: PropTypes.func
};
